import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { collectTextAnswerSnapshots } from './collectTextAnswerSnapshots';

export interface QuestionnaireTextUndoContextValue {
  /** Register pre-AI text for a field (used before generation/regeneration). */
  seedAiBaselines: (snapshots: Map<string, string>) => void;
  /** Take and clear a seeded baseline for a field when it mounts after AI replace. */
  consumeAiBaseline: (fieldKey: string) => string | undefined;
}

const QuestionnaireTextUndoContext = createContext<QuestionnaireTextUndoContextValue | null>(null);

export interface QuestionnaireTextUndoProviderProps {
  children: ReactNode;
  questionnaire: Questionnaire;
  getResponse: () => QuestionnaireResponse | undefined;
  /** Optional: parent stores captureTextUndoBaselines for AI flows. */
  onRegisterCapture?: (capture: () => void) => void;
}

export function QuestionnaireTextUndoProvider({
  children,
  questionnaire,
  getResponse,
  onRegisterCapture,
}: QuestionnaireTextUndoProviderProps): JSX.Element {
  const aiBaselinesRef = useRef<Map<string, string>>(new Map());

  const seedAiBaselines = useCallback((snapshots: Map<string, string>) => {
    aiBaselinesRef.current = new Map(snapshots);
  }, []);

  const consumeAiBaseline = useCallback((fieldKey: string): string | undefined => {
    const value = aiBaselinesRef.current.get(fieldKey);
    if (value !== undefined) {
      aiBaselinesRef.current.delete(fieldKey);
    }
    return value;
  }, []);

  const captureTextUndoBaselines = useCallback(() => {
    const response = getResponse();
    seedAiBaselines(collectTextAnswerSnapshots(questionnaire, response));
  }, [getResponse, questionnaire, seedAiBaselines]);

  useEffect(() => {
    onRegisterCapture?.(captureTextUndoBaselines);
    return () => onRegisterCapture?.(() => undefined);
  }, [captureTextUndoBaselines, onRegisterCapture]);

  const value = useMemo(
    () => ({ seedAiBaselines, consumeAiBaseline }),
    [seedAiBaselines, consumeAiBaseline],
  );

  return (
    <QuestionnaireTextUndoContext.Provider value={value}>
      {children}
    </QuestionnaireTextUndoContext.Provider>
  );
}

/** Optional outside questionnaire forms (e.g. template preview without provider). */
export function useQuestionnaireTextUndoContext(): QuestionnaireTextUndoContextValue | null {
  return useContext(QuestionnaireTextUndoContext);
}
